import { h } from 'preact'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import MiniSearch from 'minisearch'
import { t } from '../beacon-i18n.js'

/**
 * GuideScreen — client-side search over the guide site's search-index.json
 * (see plan "Guide site" section for the exact fetch URL + MiniSearch config,
 * kept in sync with the guide repo's own src/scripts/search.ts).
 */
export function GuideScreen({ store }) {
  const [query, setQuery] = useState(store.guideQuery.value)
  const [results, setResults] = useState([])
  const miniSearchRef = useRef(null)

  useEffect(() => {
    if (store.guides.value.length > 0) {
      buildIndex(store.guides.value)
      if (query.trim()) runSearch(query)
      return
    }
    if (!store.identity.guideBaseUrl) {
      store.guidesError.value = true
      return
    }

    store.guidesLoading.value = true
    store.guidesError.value = false

    fetch(`${store.identity.guideBaseUrl}/search-index.json`)
      .then((response) => {
        if (!response.ok) throw new Error(`status ${response.status}`)
        return response.json()
      })
      .then((entries) => {
        store.setGuides(entries)
        buildIndex(entries)
        if (query.trim()) runSearch(query)
      })
      .catch(() => {
        store.guidesError.value = true
      })
      .finally(() => {
        store.guidesLoading.value = false
      })
  }, [])

  function buildIndex(entries) {
    miniSearchRef.current = new MiniSearch({
      idField: 'slug',
      fields: ['title', 'category', 'excerpt'],
      storeFields: ['title', 'category', 'excerpt'],
      searchOptions: {
        boost: { title: 2 },
        fuzzy: 0.2,
        prefix: true,
      },
    })
    miniSearchRef.current.addAll(entries)
  }

  function runSearch(value) {
    if (!miniSearchRef.current || !value.trim()) {
      setResults([])
      return
    }
    setResults(miniSearchRef.current.search(value).slice(0, 12))
  }

  const handleQuery = useMemo(() => (value) => {
    setQuery(value)
    store.setGuideQuery(value)
    runSearch(value)
  }, [])

  const featured = useMemo(
    () => store.guides.value.filter((entry) => entry.featured),
    [store.guides.value]
  )

  const loading = store.guidesLoading.value
  const error   = store.guidesError.value
  const showFeatured = !loading && !error && !query.trim() && featured.length > 0

  return (
    <div class="beacon-guide-screen">
      {error ? (
        <div class="beacon-guide-unavailable">
          <i class="bi bi-search"></i>
          <p>{t('support.beacon.guide.unavailable')}</p>
        </div>
      ) : (
        <div class="beacon-guide-search">
          <div class="beacon-guide-search-input-wrap">
            <i class="bi bi-search"></i>
            <input
              type="text"
              placeholder={t('support.beacon.guide.search_placeholder')}
              value={query}
              onInput={(e) => handleQuery(e.currentTarget.value)}
              disabled={loading}
            />
          </div>

          {loading && <div class="beacon-guide-loading">{t('support.beacon.guide.loading')}</div>}

          {!loading && query.trim() && results.length === 0 && (
            <div class="beacon-guide-empty">{t('support.beacon.guide.no_results')}</div>
          )}

          {showFeatured && (
            <div class="beacon-guide-featured">
              <p class="beacon-guide-featured-label">{t('support.beacon.guide.suggested_label')}</p>
              <ul class="beacon-guide-results">
                {featured.map((result) => (
                  <li key={result.slug}>
                    <button
                      type="button"
                      class="beacon-guide-result-item"
                      onClick={() => store.openArticle(result.slug, result.title)}
                    >
                      {result.category && <span class="beacon-guide-result-category">{result.category}</span>}
                      <span class="beacon-guide-result-title">{result.title}</span>
                      <span class="beacon-guide-result-excerpt">{result.excerpt}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ul class="beacon-guide-results">
            {results.map((result) => (
              <li key={result.id}>
                <button
                  type="button"
                  class="beacon-guide-result-item"
                  onClick={() => store.openArticle(result.id, result.title)}
                >
                  {result.category && <span class="beacon-guide-result-category">{result.category}</span>}
                  <span class="beacon-guide-result-title">{result.title}</span>
                  <span class="beacon-guide-result-excerpt">{result.excerpt}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
